import { useLocale } from '@instamenu/context-i18n';
import { ComponentType, useMemo } from 'react';
import { usePermissions } from '@instamenu/context-user';
import {
  Bot,
  CalendarClock,
  ChartNoAxesCombined,
  ChefHat,
  CircleDollarSign,
  ClipboardList,
  Clock,
  Gift,
  LucideProps,
  Settings,
  Star,
  UsersRound,
} from 'lucide-react';

export type NavItemProps = {
  label: string;
  path: string;
  icon?: ComponentType<LucideProps>;
  badge?: string;
  subItems?: Array<{
    label: string;
    path: string;
    key?: string;
    icon?: ComponentType<LucideProps>;
    onClick?: () => void;
    onDelete?: () => void;
    onRename?: (title: string) => void;
    pending?: boolean;
    isActive?: boolean;
  }>;
  isActive?: boolean;
  isOpen?: boolean;
  alwaysOpen?: boolean;
  headerAction?: React.ReactNode;
  onClick?: () => void;
  isSectionLabel?: boolean;
};

export const filterAccessibleRoutes = (
  routes: NavItemProps[],
  canAccessPath: (path: string) => boolean,
) =>
  routes
    .map((route) => {
      const subItems = route.subItems?.filter((subItem) => canAccessPath(subItem.path));
      const parentAccessible = canAccessPath(route.path);

      return {
        ...route,
        path: parentAccessible ? route.path : (subItems?.[0]?.path ?? route.path),
        subItems,
      };
    })
    .filter((route) => canAccessPath(route.path) || Boolean(route.subItems?.length));

export const useNavigation = (): NavItemProps[] => {
  const { i18n } = useLocale();
  const { canAccessPath } = usePermissions();

  const routes = useMemo(() => {
    const allRoutes: NavItemProps[] = [
      {
        label: i18n.t('mainMenu.salesSummary'),
        path: '/sales-summary',
        icon: ChartNoAxesCombined,
      },
      {
        label: i18n.t('mainMenu.customers'),
        path: '/customers',
        icon: UsersRound,
      },
      {
        label: i18n.t('mainMenu.employees'),
        path: '/employees',
        icon: ChefHat,
        subItems: [
          { label: i18n.t('mainMenu.allEmployees'), path: '/employees' },
          { label: i18n.t('employees.createEmployee'), path: '/employees/create' },
        ],
      },
      {
        label: i18n.t('mainMenu.scheduledOrders'),
        path: '/scheduled-orders',
        icon: CalendarClock,
      },
      {
        label: i18n.t('mainMenu.shifts'),
        path: '/shifts',
        icon: Clock,
        subItems: [
          { label: i18n.t('mainMenu.allShifts'), path: '/shifts' },
          { label: i18n.t('shifts.addShift'), path: '/shifts/create' },
        ],
      },
      {
        label: i18n.t('mainMenu.invoices'),
        path: '/invoices',
        icon: ClipboardList,
        subItems: [
          { label: i18n.t('mainMenu.allInvoices'), path: '/invoices' },
          { label: i18n.t('invoices.createInvoice'), path: '/invoices/create' },
        ],
      },
      {
        label: i18n.t('mainMenu.payouts'),
        path: '/payouts',
        icon: CircleDollarSign,
        subItems: [
          { label: i18n.t('mainMenu.allPayouts'), path: '/payouts' },
          { label: i18n.t('payouts.createPayout'), path: '/payouts/create' },
        ],
      },
      {
        label: i18n.t('mainMenu.giftCards'),
        path: '/gift-cards',
        icon: Gift,
      },
      {
        label: i18n.t('mainMenu.loyalty'),
        path: '/loyalty',
        icon: Star,
        subItems: [
          { label: i18n.t('loyalty.settings'), path: '/loyalty/settings' },
          { label: i18n.t('loyalty.analytics._'), path: '/loyalty/analytics' },
        ],
      },
      {
        label: i18n.t('mainMenu.settings'),
        path: '/settings',
        icon: Settings,
        subItems: [
          { label: i18n.t('settings.restaurantSettings.general'), path: '/settings' },
          {
            label: i18n.t('settings.restaurantSettings.onlineOrdering'),
            path: '/settings/online-ordering',
          },
          { label: i18n.t('settings.restaurantSettings.hours'), path: '/settings/hours' },
          {
            label: i18n.t('settings.restaurantSettings.bankingAndCompliance'),
            path: '/settings/banking-and-compliance',
          },
          {
            label: i18n.t('settings.restaurantSettings.devices'),
            path: '/settings/devices',
          },
        ],
      },
      {
        label: i18n.t('mainMenu.aiAssistant'),
        path: '/ai',
        icon: Bot,
      },
    ];

    return filterAccessibleRoutes(allRoutes, canAccessPath);
  }, [i18n, canAccessPath]);

  return routes;
};
